import { useQuery } from '@tanstack/react-query';

import {
  type TeamUser,
  type TeamUserFilters,
  TeamUserSchema,
  type TeamUsers,
  TeamUsersSchema,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const useTeamUserQuery = (teamUserId: string | undefined | null) => {
  return useQuery({
    queryKey: ['teamUser', teamUserId],
    queryFn: async (): Promise<TeamUser | null> => {
      if (!teamUserId) return null;
      const response = await apiClient.get<TeamUser>(
        `/team-users/${teamUserId}`,
      );

      if (response.status === 404) {
        return null;
      }

      if (response.status !== 200) {
        logger.error('failed to fetch team user', {
          teamUserId,
          status: response.status,
        });
        throw new Error('failed to fetch team user');
      }

      const validatedData = TeamUserSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate team user data', {
          teamUserId,
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled: !!teamUserId,
  });
};

export const useTeamUsersQuery = (
  params: TeamUserFilters,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ['teamUsers', params],
    queryFn: async (): Promise<TeamUsers> => {
      const response = await apiClient.get<TeamUser[]>('/team-users', {
        params,
      });
      const validatedData = TeamUsersSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate team users data', {
          params,
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};
